import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Login from './pages/Login';
import SavedShows from './pages/SavedShows';
import JobInfo from './pages/JobInfo';
import Faces from './pages/Faces';
import ShowFlow from './pages/ShowFlow';
import Lists from './pages/Lists';
import Database from './pages/Database';
import Directions from './pages/Directions';
import Admin from './pages/Admin';
import * as api from './api';
import { connectSocket, getSocket, disconnectSocket } from './socket';

const ALL_TABS = [
  'Saved Shows',
  'Job Info',
  'Faces',
  'Show Flow',
  'Lists',
  'Database',
  'Directions',
  'Admin'
];

const VIEWER_TABS = [
  'Saved Shows',
  'Job Info',
  'Faces',
  'Show Flow',
];

function App() {
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('cvs_active_tab') || 'Saved Shows'); // eslint-disable-line no-unused-vars
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('cvs_active_tab', tab);
  };

  const handleShowChange = (id) => {
    setCurrentShowId(id);
    if (id) {
      localStorage.setItem('cvs_current_show_id', id);
    } else {
      localStorage.removeItem('cvs_current_show_id');
    }
  };
  const [currentShowId, setCurrentShowId] = useState(() => { // eslint-disable-line no-unused-vars
    const saved = localStorage.getItem('cvs_current_show_id');
    return saved ? parseInt(saved) : null;
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');
  const [changePasswordSuccess, setChangePasswordSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem('cvs_user');
    return saved ? JSON.parse(saved) : null;
  });

  const [shows, setShows] = useState([]);
  const [database, setDatabaseState] = useState([]);
  const [lists, setListsState] = useState({});
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [presence, setPresence] = useState([]);
  const [socketConnected, setSocketConnected] = useState(false);

    // Connect the socket when logged in
  React.useEffect(() => {
    if (!currentUser) return;
    const s = connectSocket();
    if (!s) return;

    const onPresence = (members) => setPresence(members);
    const onConnect = () => setSocketConnected(true);
    const onDisconnect = () => { setSocketConnected(false); setPresence([]); };
    const onConnectError = (err) => {
      setSocketConnected(false);
      console.warn('Socket connection error:', err.message);
    };

    setSocketConnected(s.connected);

    // Poll as a safety net in case a connect/disconnect event is missed
    const statusPoll = setInterval(() => {
      setSocketConnected(prev => (prev !== s.connected ? s.connected : prev));
    }, 2000);

    s.on('presence', onPresence);
    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    s.on('connect_error', onConnectError);

    return () => {
      clearInterval(statusPoll);
      s.off('presence', onPresence);
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      s.off('connect_error', onConnectError);
    };
  }, [currentUser]);

  // Join/leave the show room as the current show changes.
  // Also re-join whenever the socket (re)connects.
  React.useEffect(() => {
    const s = getSocket();
    if (!s || !currentShowId) return;

    const join = () => s.emit('joinShow', currentShowId);

    if (s.connected) join();
    s.on('connect', join);

    return () => {
      s.off('connect', join);
      s.emit('leaveShow', currentShowId);
    };
  }, [currentShowId, currentUser]);
    const handleLogout = useCallback(() => {
    disconnectSocket();
    localStorage.removeItem('cvs_token');
    localStorage.removeItem('cvs_user');
    setCurrentUser(null);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const promises = [api.getShows(), api.getPresenters(), api.getLists()];
      if (currentUser?.role === 'admin' || currentUser?.role === 'editor') promises.push(api.getUsers());
      const [showsData, presentersData, listsData, usersData] = await Promise.all(promises);
      if (showsData.error === 'Invalid token' || presentersData.error === 'Invalid token') {
        handleLogout();
        return;
      }
      setShows(Array.isArray(showsData) ? showsData : []);
      setDatabaseState(Array.isArray(presentersData) ? presentersData : []);
      setListsState(listsData || {});
      if (usersData) setAllUsers(Array.isArray(usersData) ? usersData : []);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
    setLoading(false);
  }, [handleLogout, currentUser?.role]);

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser, loadData]);

  const handleLogin = (user) => {
    setCurrentUser(user);
  };

  const handleChangePassword = async (currentPassword, newPassword) => {
    try {
      const res = await fetch(
        `${window.location.hostname === 'localhost' ? 'http://localhost:4000' : 'https://showflow.creativevideoservices.net'}/api/users/${currentUser.id}/change-password`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('cvs_token')}`
          },
          body: JSON.stringify({ currentPassword, newPassword })
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setChangePasswordError(data.error || 'Failed to change password.');
        return;
      }
      setChangePasswordSuccess('Password changed successfully!');
      setTimeout(() => {
        setShowChangePassword(false);
        setChangePasswordSuccess('');
        setChangePasswordError('');
      }, 2000);
    } catch (err) {
      setChangePasswordError('Server error. Please try again.');
    }
  };

  const currentShow = shows.find(s => s.id === currentShowId);

  // ─── SHOWS ───────────────────────────────────────────────────────────────────

  const createShow = async (name) => {
    const newShow = await api.createShow(name);
    setShows(prev => [newShow, ...prev]);
    handleShowChange(newShow.id);
    handleTabChange('Job Info');
  };

  const deleteShow = async (id) => {
    await api.deleteShow(id);
    setShows(prev => prev.filter(s => s.id !== id));
    if (currentShowId === id) {
      handleShowChange(null);
      handleTabChange('Saved Shows');
    }
  };
  const duplicateShow = async (id) => {
    const original = shows.find(s => s.id === id);
    if (!original) return;
    const newShow = await api.createShow(`${original.name} (Copy)`);
    await api.updateShow(newShow.id, {
      jobInfo: original.jobInfo,
      presenterOrder: original.presenterOrder,
      showFlowRows: original.showFlowRows,
      columnVisibility: original.columnVisibility,
      columnOrder: original.columnOrder,
      notesLabel: original.notesLabel,
      custom2Label: original.custom2Label,
      custom3Label: original.custom3Label,
      custom4Label: original.custom4Label,
      versionNumber: '1.0'
    });
    const updatedShows = await api.getShows();
    setShows(updatedShows);
  };
  const renameShow = async (id, newName) => {
    await api.updateShow(id, { name: newName });
    setShows(prev => prev.map(s => s.id === id ? { ...s, name: newName } : s));
  };
  const openShow = (id) => {
    handleShowChange(id);
    handleTabChange('Job Info');
  };

  const updateCurrentShow = useCallback(async (updater) => {
    if (!currentShowId) return;
    const now = new Date().toISOString();
    let previousShow = null;
    let updated = null;
    let baseShow = null;

    // Compute from the freshest state, not a stale closure
    setShows(prev => {
      const cur = prev.find(s => s.id === currentShowId);
      if (!cur) return prev;
      previousShow = cur;
      baseShow = cur;
      updated = updater(cur);
      return prev.map(s => s.id === currentShowId ? { ...s, ...updated, lastSaved: now } : s);
    });

    if (!updated || !baseShow) return;
    const currentShow = baseShow;

    const result = await api.updateShow(currentShowId, { ...updated, revision: currentShow.revision });

    if (result && result.conflict) {
      // Someone else saved first. Refetch the server's version, then reapply our change on top.
      try {
        const freshShows = await api.getShows();
        const fresh = Array.isArray(freshShows) ? freshShows.find(s => s.id === currentShowId) : null;
        if (!fresh) {
          setShows(prev => prev.map(s => s.id === currentShowId ? previousShow : s));
          return;
        }
        const merged = { ...fresh, ...updated };
        const retry = await api.updateShow(currentShowId, { ...updated, revision: fresh.revision });
        setShows(prev => prev.map(s => {
          if (s.id !== currentShowId) return s;
          return { ...merged, lastSaved: now, revision: retry && retry.revision ? retry.revision : fresh.revision + 1 };
        }));
      } catch (err) {
        setShows(prev => prev.map(s => s.id === currentShowId ? previousShow : s));
      }
      return;
    }

    if (result && result.error) {
      // Server rejected the save for another reason — roll back
      setShows(prev => prev.map(s => s.id === currentShowId ? previousShow : s));
      alert(result.error);
      return;
    }

    // Success — record the new revision
    if (result && result.revision) {
      setShows(prev => prev.map(s => s.id === currentShowId ? { ...s, revision: result.revision } : s));
    }
  }, [currentShowId]);

  const setJobInfo = useCallback((updater) => {
    updateCurrentShow(s => ({
      jobInfo: typeof updater === 'function' ? updater(s.jobInfo) : updater
    }));
  }, [updateCurrentShow]);

  const saveTimerRef = React.useRef(null);

  const setShowFlowRows = useCallback((updater) => {
    const now = new Date().toISOString();

    // Apply locally right away so typing stays responsive
    setShows(prev => prev.map(s => {
      if (s.id !== currentShowId) return s;
      const nextRows = typeof updater === 'function' ? updater(s.showFlowRows) : updater;
      return { ...s, showFlowRows: nextRows, lastSaved: now };
    }));

    // Debounce the actual save
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      updateCurrentShow(s => ({ showFlowRows: s.showFlowRows }));
      saveTimerRef.current = null;
    }, 600);
  }, [updateCurrentShow, currentShowId]);
  const setNotesLabel = useCallback((label) => {
    updateCurrentShow(s => ({ notesLabel: label }));
    if (currentShowId) {
      api.updateShow(currentShowId, { notesLabel: label });
    }
  }, [updateCurrentShow, currentShowId]);
  const setDropdownsEnabled = useCallback((enabled) => {
    // Apply immediately so the UI flips right away
    setShows(prev => prev.map(s => s.id === currentShowId ? { ...s, dropdownsEnabled: enabled } : s));
    if (currentShowId) {
      api.updateShow(currentShowId, { dropdownsEnabled: enabled });
    }
  }, [currentShowId]);

  // Applied when the change came from another user — no save needed
  const applyDropdownsEnabled = useCallback((enabled) => {
    setShows(prev => prev.map(s => s.id === currentShowId ? { ...s, dropdownsEnabled: enabled } : s));
  }, [currentShowId]);
  const setTimerSettings = useCallback((settings) => {
    updateCurrentShow(s => ({ ...settings }));
    if (currentShowId) {
      api.updateShow(currentShowId, settings);
    }
  }, [updateCurrentShow, currentShowId]);
  const setVersionNumber = useCallback((version) => {
    updateCurrentShow(s => ({ versionNumber: version }));
    if (currentShowId) {
      api.updateShow(currentShowId, { versionNumber: version });
    }
  }, [updateCurrentShow, currentShowId]);

  const setCustom2Label = useCallback((label) => {
    updateCurrentShow(s => ({ custom2Label: label }));
    if (currentShowId) {
      api.updateShow(currentShowId, { custom2Label: label });
    }
  }, [updateCurrentShow, currentShowId]);

  const setCustom3Label = useCallback((label) => {
    updateCurrentShow(s => ({ custom3Label: label }));
    if (currentShowId) {
      api.updateShow(currentShowId, { custom3Label: label });
    }
  }, [updateCurrentShow, currentShowId]);

  const setCustom4Label = useCallback((label) => {
    updateCurrentShow(s => ({ custom4Label: label }));
    if (currentShowId) {
      api.updateShow(currentShowId, { custom4Label: label });
    }
  }, [updateCurrentShow, currentShowId]);
  const setColumnVisibility = useCallback((visibility) => {
    updateCurrentShow(s => ({ columnVisibility: visibility }));
    if (currentShowId) {
      api.updateShow(currentShowId, { columnVisibility: visibility });
    }
  }, [updateCurrentShow, currentShowId]);
const handleAddPermission = useCallback(async (userId, permission) => {
    if (!currentShowId) return;
    await api.addShowPermission(currentShowId, userId, permission);
    setShows(prev => prev.map(s => {
      if (s.id !== currentShowId) return s;
      const user = allUsers.find(u => u.id === parseInt(userId));
      if (!user) return s;
      const editors = permission === 'editor' 
        ? [...(s.editors || []), { id: user.id, name: user.name, email: user.email }]
        : s.editors;
      return { ...s, editors };
    }));
  }, [currentShowId, allUsers]);

  const handleRemovePermission = useCallback(async (userId) => {
    if (!currentShowId) return;
    await api.removeShowPermission(currentShowId, userId);
    setShows(prev => prev.map(s => {
      if (s.id !== currentShowId) return s;
      return { ...s, editors: (s.editors || []).filter(e => e.id !== userId) };
    }));
  }, [currentShowId]);
  // eslint-disable-next-line no-unused-vars
  const handleChangeOwner = useCallback(async (userId) => {
    if (!currentShowId) return;
    await api.addShowPermission(currentShowId, userId, 'owner');
    const newOwner = allUsers.find(u => u.id === parseInt(userId));
    if (newOwner) {
      setShows(prev => prev.map(s => {
        if (s.id !== currentShowId) return s;
        return { ...s, ownerName: newOwner.name };
      }));
    }
  }, [currentShowId, allUsers]);
  const setColumnOrder = useCallback((order) => {
    updateCurrentShow(s => ({ columnOrder: order }));
    if (currentShowId) {
      api.updateShow(currentShowId, { columnOrder: order });
    }
  }, [updateCurrentShow, currentShowId]);

  const setShowPresenters = useCallback((updater) => {
    updateCurrentShow(s => ({
      presenterOrder: typeof updater === 'function' ? updater(s.presenterOrder) : updater
    }));
  }, [updateCurrentShow]);

  // ─── DATABASE ─────────────────────────────────────────────────────────────────

  const setDatabase = async (updater) => {
    const updated = typeof updater === 'function' ? updater(database) : updater;
    setDatabaseState(updated);
  };

  // ─── LISTS ────────────────────────────────────────────────────────────────────

  const setLists = async (updater) => {
    const updated = typeof updater === 'function' ? updater(lists) : updater;
    setListsState(updated);
    await api.updateLists(updated);
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────────

  if (!currentUser) {
    return <Login onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#1a1a2e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  const renderPage = () => {
    switch (activeTab) {
      case 'Saved Shows':
        return (
          <SavedShows
            shows={shows}
            onCreateShow={createShow}
            onOpenShow={openShow}
            onDeleteShow={deleteShow}
            onDuplicateShow={duplicateShow}
            onRenameShow={renameShow}
            currentShowId={currentShowId}
            currentUser={currentUser}
          />
        );
      case 'Job Info':
        return currentShow ? (
          <JobInfo
            database={database}
            jobInfo={currentShow.jobInfo}
            setJobInfo={setJobInfo}
            showPresenters={currentShow.presenterOrder}
            setShowPresenters={setShowPresenters}
            currentShow={currentShow}
            currentUser={currentUser}
            allUsers={allUsers}
            onAddPermission={handleAddPermission}
            onRemovePermission={handleRemovePermission}
            onChangeOwner={handleChangeOwner}
            viewOnly={currentUser.role === 'viewer' || currentShow.userPermission === 'viewer'}
          />
        ) : <NoShowSelected onGoToShows={() => handleTabChange('Saved Shows')} />;
      case 'Faces':
        return currentShow ? (
          <Faces
            currentShowId={currentShowId}
            presenters={currentShow.presenterOrder.map(p => {
              const latest = database.find(d => d.id === p.id);
              return latest ? { ...p, ...latest } : p;
            })} />
        ) : <NoShowSelected onGoToShows={() => handleTabChange('Saved Shows')} />;
      case 'Show Flow':
        return currentShow ? (
          currentUser.role === 'viewer' || currentShow.userPermission === 'viewer' ? (
            <ShowFlow
              showPresenters={currentShow.presenterOrder.map(p => {
                const latest = database.find(d => d.id === p.id);
                return latest ? { ...p, ...latest } : p;
              })}
              rows={currentShow.showFlowRows}
              setRows={setShowFlowRows}
              jobInfo={currentShow.jobInfo}
              lists={lists}
              notesLabel={currentShow.notesLabel || ''}
              setNotesLabel={setNotesLabel}
              custom2Label={currentShow.custom2Label || ''}
              setCustom2Label={setCustom2Label}
              custom3Label={currentShow.custom3Label || ''}
              setCustom3Label={setCustom3Label}
              custom4Label={currentShow.custom4Label || ''}
              setCustom4Label={setCustom4Label}
              columnVisibility={currentShow.columnVisibility || null}
              setColumnVisibility={setColumnVisibility}
              columnOrder={currentShow.columnOrder || null}
              setColumnOrder={setColumnOrder}
              lastSaved={currentShow.lastSaved || null}
              versionNumber={currentShow.versionNumber || '1.0'}
              setVersionNumber={setVersionNumber}
              currentShowId={currentShowId}
              currentUserId={currentUser.id}
              socketConnected={socketConnected}
              timerEnabled={currentShow.timerEnabled !== false}
              timerMode={currentShow.timerMode || 'hold'}
              setTimerSettings={setTimerSettings}
              dropdownsEnabled={currentShow.dropdownsEnabled !== false}
              setDropdownsEnabled={setDropdownsEnabled}
              applyDropdownsEnabled={applyDropdownsEnabled}
              viewOnly={true}
            />
          ) : (
            <ShowFlow
              showPresenters={currentShow.presenterOrder.map(p => {
                const latest = database.find(d => d.id === p.id);
                return latest ? { ...p, ...latest } : p;
              })}
              rows={currentShow.showFlowRows}
              setRows={setShowFlowRows}
              jobInfo={currentShow.jobInfo}
              lists={lists}
              notesLabel={currentShow.notesLabel || ''}
              setNotesLabel={setNotesLabel}
              custom2Label={currentShow.custom2Label || ''}
              setCustom2Label={setCustom2Label}
              custom3Label={currentShow.custom3Label || ''}
              setCustom3Label={setCustom3Label}
              custom4Label={currentShow.custom4Label || ''}
              setCustom4Label={setCustom4Label}
              columnVisibility={currentShow.columnVisibility || null}
              setColumnVisibility={setColumnVisibility}
              columnOrder={currentShow.columnOrder || null}
              setColumnOrder={setColumnOrder}
              lastSaved={currentShow.lastSaved || null}
              versionNumber={currentShow.versionNumber || '1.0'}
              setVersionNumber={setVersionNumber}
              currentShowId={currentShowId}
              currentUserId={currentUser.id}
              socketConnected={socketConnected}
              timerEnabled={currentShow.timerEnabled !== false}
              timerMode={currentShow.timerMode || 'hold'}
              setTimerSettings={setTimerSettings}
              dropdownsEnabled={currentShow.dropdownsEnabled !== false}
              setDropdownsEnabled={setDropdownsEnabled}
              applyDropdownsEnabled={applyDropdownsEnabled}
            />
          )
        ) : <NoShowSelected onGoToShows={() => handleTabChange('Saved Shows')} />;
      case 'Lists':
        return <Lists lists={lists} setLists={setLists} />;
      case 'Database':
        return (
          <Database
            database={database}
            setDatabase={setDatabase}
          />
        );
      case 'Directions':
        return <Directions />;
      case 'Admin':
        return <Admin currentUser={currentUser} />;
      default:
        return (
          <SavedShows
            shows={shows}
            onCreateShow={createShow}
            onOpenShow={openShow}
            onDeleteShow={deleteShow}
            onDuplicateShow={duplicateShow}
            onRenameShow={renameShow}
            currentShowId={currentShowId}
            currentUser={currentUser}
          />
        );
    }
  };

  return (
    <div className="app">
      <div className="header">
        <div className="logo" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="CVS Logo" style={{ height: '36px', width: 'auto' }} />
          CVS Show Flow & Faces Creator
        </div>
        <nav className="nav">
          {(currentUser.role === 'viewer' ? VIEWER_TABS : ALL_TABS).map(tab => (
            <button
              key={tab}
              className={`nav-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => handleTabChange(tab)}
            >
              {tab}
            </button>
          ))}
        </nav>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '16px' }}>
                    {currentShow && (
            <span style={{ color: '#90cdf4', fontSize: '13px' }}>
              📁 {currentShow.name}
            </span>
          )}
                    {currentShow && presence.length > 1 && (
            <span
              style={{
                color: '#68d391',
                fontSize: '12px',
                backgroundColor: 'rgba(104,211,145,0.15)',
                border: '1px solid rgba(104,211,145,0.4)',
                borderRadius: '12px',
                padding: '2px 10px',
                whiteSpace: 'nowrap',
                maxWidth: '320px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
              title={presence.map(p => p.name).join(', ')}
            >
              👥 {presence.map(p => p.name).join(', ')}
            </span>
          )}
          {!socketConnected && (
            <span
              title="Live sync is offline. Presence and Show Mode following will not work. Try refreshing, or sign out and back in."
              style={{
                color: '#feb2b2',
                fontSize: '12px',
                fontWeight: '700',
                backgroundColor: 'rgba(229,62,62,0.2)',
                border: '1px solid rgba(229,62,62,0.6)',
                borderRadius: '12px',
                padding: '2px 10px',
                whiteSpace: 'nowrap'
              }}
            >
              ⚠️ Live sync offline
            </span>
          )}
          <span style={{ color: '#90cdf4', fontSize: '13px' }}>
            {currentUser.name}
          </span>
          <button
            onClick={() => setShowChangePassword(true)}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #4a5568',
              borderRadius: '6px',
              color: '#90cdf4',
              padding: '6px 12px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            🔑 Password
          </button>
          <button
            onClick={handleLogout}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #4a5568',
              borderRadius: '6px',
              color: '#90cdf4',
              padding: '6px 12px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
      <div className="page-content">
        {renderPage()}
      </div>
      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => { setShowChangePassword(false); setChangePasswordError(''); setChangePasswordSuccess(''); }}
          onSubmit={handleChangePassword}
          error={changePasswordError}
          success={changePasswordSuccess}
        />
      )}
    </div>
  );
}

function ChangePasswordModal({ onClose, onSubmit, error, success }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState('');

  const handleSubmit = () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setLocalError('All fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setLocalError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 6) {
      setLocalError('New password must be at least 6 characters.');
      return;
    }
    setLocalError('');
    onSubmit(currentPassword, newPassword);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000
    }}>
      <div className="card" style={{ width: '400px' }}>
        <h2 style={{ marginBottom: '20px', color: '#1a1a2e', fontSize: '20px' }}>Change Password</h2>

        {(error || localError) && (
          <div style={{
            backgroundColor: '#fff5f5', border: '1px solid #fed7d7',
            borderRadius: '6px', padding: '10px', marginBottom: '16px',
            color: '#c53030', fontSize: '13px'
          }}>
            {localError || error}
          </div>
        )}

        {success && (
          <div style={{
            backgroundColor: '#f0fff4', border: '1px solid #9ae6b4',
            borderRadius: '6px', padding: '10px', marginBottom: '16px',
            color: '#276749', fontSize: '13px'
          }}>
            {success}
          </div>
        )}

        {[
          { label: 'Current Password', value: currentPassword, setter: setCurrentPassword },
          { label: 'New Password', value: newPassword, setter: setNewPassword },
          { label: 'Confirm New Password', value: confirmPassword, setter: setConfirmPassword }
        ].map(field => (
          <div key={field.label} style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '6px' }}>
              {field.label}
            </label>
            <input
              type="password"
              value={field.value}
              onChange={e => field.setter(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '6px',
                border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
        ))}

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '10px', borderRadius: '6px',
              border: '1px solid #e2e8f0', background: 'white',
              cursor: 'pointer', fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              flex: 1, padding: '10px', borderRadius: '6px',
              border: 'none', background: '#1a1a2e', color: 'white',
              cursor: 'pointer', fontSize: '14px', fontWeight: '600'
            }}
          >
            Change Password
          </button>
        </div>
      </div>
    </div>
  );
}

function NoShowSelected({ onGoToShows }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 0' }}>
      <p style={{ fontSize: '48px', marginBottom: '16px' }}>📁</p>
      <h2 style={{ color: '#1a1a2e', marginBottom: '8px' }}>No Show Selected</h2>
      <p style={{ color: '#718096', marginBottom: '24px' }}>Go to Saved Shows to create or open a show.</p>
      <button
        onClick={onGoToShows}
        style={{
          backgroundColor: '#4299e1',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          padding: '10px 24px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        Go to Saved Shows
      </button>
    </div>
  );
}

export default App;